import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import MemberNav from './MemberNav';
import { GallerySkeleton } from './LoadingSkeleton';
import { speciesLabel } from '../../utils/memberStats';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { formatClubDate } from '../../utils/clubTime';
import { catchRotation, rotatedFrameStyle, rotatedImageStyle } from '../../utils/catchRotation';

const client = generateClient({ authMode: 'userPool' });

interface CatchGalleryProps {
  onNavigate: (page: 'dashboard' | 'profile' | 'competitions' | 'catches' | 'my-stats' | 'my-fishing' | 'season-championship' | 'home') => void;
  embedded?: boolean;
}

const SPECIES_OPTIONS = ['FLATHEAD', 'BREAM', 'JEWFISH', 'MANGROVE_JACK', 'WHITING', 'TAILOR', 'TREVALLY', 'OTHER'];

const CatchGallery: React.FC<CatchGalleryProps> = ({ onNavigate, embedded }) => {
  const { userId } = useAuth();
  const [catches, setCatches] = useState<any[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string | null>>({});
  const [selectedCatch, setSelectedCatch] = useState<any | null>(null);
  const [filterSpecies, setFilterSpecies] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('me');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [member, setMember] = useState<any | null>(null);

  const fetchData = async () => {
    if (!userId) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      // Fetch member record via the narrow server-side projection
      const profileResult: any = await (client.queries as any).getOwnMemberProfile({});
      const profileResponse = parseLambdaResponse(profileResult?.data);
      const m = profileResponse?.success ? profileResponse.own : null;
      setMember(m);

      // Fetch all catches with nextToken loop
      let allCatches: any[] = [];
      let nextToken: string | null = null;
      do {
        const result: any = await client.models.CatchSubmission.list({
          filter: { memberSub: { eq: userId } },
          ...(nextToken ? { nextToken } : {}),
        });
        allCatches = allCatches.concat(result.data || []);
        nextToken = result.nextToken ?? null;
      } while (nextToken);

      const approved = allCatches.filter((c: any) => c.status === 'APPROVED');
      setCatches(approved);

      // Load photo URLs in parallel
      const urls: Record<string, string | null> = {};
      await Promise.allSettled(
        approved.filter((c: any) => c.photoS3Key).map(async (c: any) => {
          try {
            const result = await (client.queries as any).generateDownloadUrl({ s3Key: c.photoS3Key });
            const data = parseLambdaResponse(result?.data);
            urls[c.id] = data?.downloadUrl || null;
          } catch {
            urls[c.id] = null;
          }
        })
      );
      setPhotoUrls(urls);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load catches');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [userId]);

  // Parse family members
  let familyMembers: Array<{ name: string }> = [];
  if (member?.membershipType === 'FAMILY' && member?.familyMembers) {
    try {
      familyMembers = typeof member.familyMembers === 'string' ? JSON.parse(member.familyMembers) : member.familyMembers;
    } catch { /* ignore */ }
  }

  // Filter catches
  const familyNames = familyMembers.map((fm: any) => fm.name);
  let filtered = catches;
  if (activeTab === 'me') {
    filtered = filtered.filter((c: any) => !familyNames.includes(c.competitorName));
  } else {
    filtered = filtered.filter((c: any) => c.competitorName === activeTab);
  }
  if (filterSpecies) filtered = filtered.filter((c: any) => c.species === filterSpecies);
  if (filterYear) filtered = filtered.filter((c: any) => c.submissionTimestamp && new Date(c.submissionTimestamp).getFullYear() === filterYear);

  const years = Array.from(new Set(catches.filter((c: any) => c.submissionTimestamp).map((c: any) => new Date(c.submissionTimestamp).getFullYear()))).sort((a: number, b: number) => b - a);

  if (isLoading) {
    if (embedded) return <GallerySkeleton />;
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-600">Loading your catches…</div>
      </div>
    );
  }

  if (loadError) {
    if (embedded) return <div className="bg-red-50 border border-red-200 rounded-md p-4"><p className="text-red-800">{loadError}</p><button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">Try Again</button></div>;
    return (
      <div className="min-h-screen bg-gray-50">
        <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{loadError}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium">Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  const content = (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Catches</h1>

        {/* Family tabs */}
        {familyMembers.length > 0 && (
          <div className="flex gap-2 mb-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('me')}
              className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium ${activeTab === 'me' ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700'}`}
            >Me</button>
            {familyMembers.map((fm: any) => (
              <button
                key={fm.name}
                onClick={() => setActiveTab(fm.name)}
                className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap ${activeTab === fm.name ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700'}`}
              >{fm.name}</button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setFilterSpecies(null)} className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium ${!filterSpecies ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700'}`}>All</button>
          {SPECIES_OPTIONS.map((s) => (
            <button key={s} onClick={() => setFilterSpecies(s)} className={`min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium ${filterSpecies === s ? 'bg-fishing-blue text-white' : 'bg-gray-100 text-gray-700'}`}>{speciesLabel(s)}</button>
          ))}
          <select
            value={filterYear ?? ''}
            onChange={(e) => setFilterYear(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-1 border border-gray-300 rounded-md text-xs"
            aria-label="Filter by year"
          >
            <option value="">All Years</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3">🎣</div>
            <p className="text-gray-600">No approved catches yet — get out there and catch something!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((c: any) => (
              <div key={c.id} onClick={() => setSelectedCatch(c)} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow">
                {photoUrls[c.id] ? (
                  <div style={rotatedFrameStyle(catchRotation(c))} className="w-full h-36 overflow-hidden">
                    <img
                      src={photoUrls[c.id]!}
                      alt={`${speciesLabel(c.species)} catch`}
                      className="w-full h-36 object-cover"
                      style={rotatedImageStyle(catchRotation(c))}
                    />
                  </div>
                ) : (
                  <div className="w-full h-36 bg-gray-100 flex items-center justify-center text-4xl">🐟</div>
                )}
                <div className="p-3">
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{speciesLabel(c.species)}</span>
                  <div className="text-sm font-medium text-gray-900 mt-1">{c.lengthCm}cm</div>
                  <div className="text-xs text-gray-500">{c.submissionTimestamp ? formatClubDate(c.submissionTimestamp) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Detail modal */}
      {selectedCatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCatch(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e: any) => e.stopPropagation()}>
            {photoUrls[selectedCatch.id] ? (
              <div style={rotatedFrameStyle(catchRotation(selectedCatch))} className="w-full h-64 overflow-hidden rounded-t-lg">
                <img
                  src={photoUrls[selectedCatch.id]!}
                  alt={`${speciesLabel(selectedCatch.species)} catch`}
                  className="w-full h-64 object-cover"
                  style={rotatedImageStyle(catchRotation(selectedCatch))}
                />
              </div>
            ) : (
              <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-6xl rounded-t-lg">🐟</div>
            )}
            <div className="p-6 space-y-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex px-2 py-0.5 text-sm font-medium rounded-full bg-blue-100 text-blue-800">{speciesLabel(selectedCatch.species)}</span>
                <span className="text-lg font-bold">{selectedCatch.lengthCm}cm</span>
              </div>
              <div className="text-sm text-gray-600">{selectedCatch.submissionTimestamp ? formatClubDate(selectedCatch.submissionTimestamp, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}</div>
              <div className="text-sm text-gray-600">{selectedCatch.keptOrReleased === 'KEPT' ? 'Kept' : selectedCatch.keptOrReleased === 'RELEASED' ? 'Released' : '—'}</div>
              <div className="text-sm text-gray-600">{selectedCatch.gpsVerified ? 'GPS Verified ✓' : 'Not GPS Verified'}</div>
              <button onClick={() => setSelectedCatch(null)} className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-gray-50">
      <MemberNav currentPage={"dashboard" as any} onNavigate={onNavigate} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </div>
    </div>
  );
};

export default CatchGallery;
